import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Upload, FileText, Download, Trash2, Phone, Mail, MapPin, Eye, Briefcase, User, FolderDown, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const ClienteDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    tipo_documento: '',
    descricao: ''
  });
  const [editData, setEditData] = useState({
    tipo_documento: '',
    descricao: ''
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clienteRes, documentosRes, processosRes] = await Promise.all([
        api.getCliente(id),
        api.getDocumentosCliente(id),
        api.getProcessosCliente(id)
      ]);
      setCliente(clienteRes.data);
      setDocumentos(documentosRes.data);
      setProcessos(processosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Selecione um arquivo');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      // Sempre enviar os campos, mesmo que vazios
      formData.append('tipo_documento', uploadData.tipo_documento || '');
      formData.append('descricao', uploadData.descricao || '');
      
      // Log para debug
      console.log('Enviando upload:', {
        file: selectedFile.name,
        tipo_documento: uploadData.tipo_documento,
        descricao: uploadData.descricao
      });

      await api.uploadDocumento(id, formData);
      toast.success('Documento enviado com sucesso!');
      setIsUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadData({ tipo_documento: '', descricao: '' });
      loadData();
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error('Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setEditData({
      tipo_documento: doc.tipo_documento || '',
      descricao: doc.descricao || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingDoc) return;

    setSaving(true);
    try {
      await api.updateDocumento(editingDoc.id, editData);
      toast.success('Documento atualizado com sucesso!');
      setIsEditDialogOpen(false);
      setEditingDoc(null);
      loadData();
    } catch (error) {
      console.error('Erro ao atualizar documento:', error);
      toast.error('Erro ao atualizar documento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (documentoId) => {
    if (!window.confirm('Deseja realmente excluir este documento?')) return;

    try {
      await api.deleteDocumento(documentoId);
      toast.success('Documento excluído com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      toast.error('Erro ao excluir documento');
    }
  };

  const handleDownload = (documentoId, nomeArquivo) => {
    const token = localStorage.getItem('token');
    const downloadUrl = api.downloadDocumento(documentoId);
    
    // Criar link temporário para download
    const link = document.createElement('a');
    link.href = `${downloadUrl}?token=${token}`;
    link.download = nomeArquivo;
    link.click();
  };

  const handleDownloadAll = async () => {
    if (documentos.length === 0) return;
    
    toast.info('Iniciando download dos documentos...');
    
    // Download de cada documento
    for (const doc of documentos) {
      const token = localStorage.getItem('token');
      const downloadUrl = api.downloadDocumento(doc.id);
      
      try {
        const response = await fetch(`${downloadUrl}?token=${token}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.nome_arquivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Pequeno delay entre downloads para não sobrecarregar
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Erro ao baixar documento:', doc.nome_arquivo);
      }
    }
    
    toast.success(`${documentos.length} documento(s) baixado(s)!`);
  };

  const handlePreview = (doc) => {
    const ext = doc.nome_arquivo.split('.').pop().toLowerCase();
    const previewableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
    
    if (previewableExtensions.includes(ext)) {
      setPreviewDoc(doc);
      setIsPreviewOpen(true);
    } else {
      toast.info('Este tipo de arquivo não suporta preview. Use o botão de download.');
    }
  };

  const getPreviewUrl = (documentoId) => {
    const token = localStorage.getItem('token');
    return `${api.downloadDocumento(documentoId)}?token=${token}`;
  };

  const isPreviewable = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      'pdf': '📄',
      'doc': '📝',
      'docx': '📝',
      'xls': '📊',
      'xlsx': '📊',
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'gif': '🖼️',
      'mp3': '🎵',
      'wav': '🎵',
      'mp4': '🎬',
      'avi': '🎬',
      'zip': '🗜️',
      'rar': '🗜️'
    };
    return iconMap[ext] || '📎';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return <div className="p-8" data-testid="cliente-detalhes-loading">Carregando...</div>;
  }

  if (!cliente) {
    return <div className="p-8">Cliente não encontrado</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="cliente-detalhes-page">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/clientes')} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-semibold text-slate-900">{cliente.nome_completo}</h1>
          <p className="text-slate-600 mt-1">{cliente.cpf_cnpj}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Informações do Cliente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {cliente.rg && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm text-slate-600">RG</p>
                    <p className="text-slate-900">{cliente.rg}</p>
                  </div>
                </div>
              )}
              {cliente.profissao && (
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm text-slate-600">Profissão</p>
                    <p className="text-slate-900">{cliente.profissao}</p>
                  </div>
                </div>
              )}
              {cliente.estado_civil && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm text-slate-600">Estado Civil</p>
                    <p className="text-slate-900">{cliente.estado_civil}</p>
                  </div>
                </div>
              )}
              {cliente.telefone_principal && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm text-slate-600">Telefone Principal</p>
                    <p className="text-slate-900">{cliente.telefone_principal}</p>
                  </div>
                </div>
              )}
              {cliente.telefone_secundario && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm text-slate-600">Telefone Secundário</p>
                    <p className="text-slate-900">{cliente.telefone_secundario}</p>
                  </div>
                </div>
              )}
              {cliente.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm text-slate-600">E-mail</p>
                    <p className="text-slate-900">{cliente.email}</p>
                  </div>
                </div>
              )}
              {(cliente.cidade || cliente.estado) && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm text-slate-600">Localização</p>
                    <p className="text-slate-900">
                      {cliente.cidade && cliente.estado 
                        ? `${cliente.cidade}/${cliente.estado}`
                        : cliente.cidade || cliente.estado}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {cliente.endereco && (
              <div>
                <p className="text-sm text-slate-600">Endereço</p>
                <p className="text-slate-900">{cliente.endereco}</p>
              </div>
            )}
            {cliente.observacoes && (
              <div>
                <p className="text-sm text-slate-600">Observações</p>
                <p className="text-slate-900">{cliente.observacoes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-slate-900 hover:bg-slate-800" data-testid="upload-button">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload de Documento
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload de Documento</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4">
                  <div>
                    <Label htmlFor="file">Arquivo *</Label>
                    <Input
                      id="file"
                      type="file"
                      onChange={handleFileChange}
                      required
                      data-testid="file-input"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.mp3,.wav,.mp4,.avi,.zip,.rar"
                    />
                    {selectedFile && (
                      <p className="text-sm text-slate-600 mt-1">
                        Arquivo selecionado: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="tipo_documento">Tipo de Documento</Label>
                    <Input
                      id="tipo_documento"
                      placeholder="Ex: Contrato, RG, CPF, Comprovante"
                      value={uploadData.tipo_documento}
                      onChange={(e) => setUploadData({ ...uploadData, tipo_documento: e.target.value })}
                      data-testid="tipo-documento-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="descricao">Descrição</Label>
                    <textarea
                      id="descricao"
                      className="w-full min-h-[80px] p-2 border rounded-md"
                      placeholder="Descreva o documento..."
                      value={uploadData.descricao}
                      onChange={(e) => setUploadData({ ...uploadData, descricao: e.target.value })}
                      data-testid="descricao-input"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-slate-900 hover:bg-slate-800" 
                      disabled={uploading}
                      data-testid="submit-upload-button"
                    >
                      {uploading ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Histórico de Processos */}
      {processos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Processos ({processos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {processos.map((processo) => (
                <div
                  key={processo.id}
                  className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="cliente-processo-item"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{processo.numero_processo}</p>
                      <p className="text-sm text-slate-600 mt-1">{processo.tipo_acao}</p>
                      <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${
                        processo.status === 'Em andamento' ? 'bg-blue-100 text-blue-700' :
                        processo.status === 'Encerrado' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {processo.status}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/processos/${processo.id}`)}
                    >
                      Ver Detalhes
                    </Button>
                  </div>
                  
                  {/* Informações Financeiras */}
                  <div className="grid grid-cols-3 gap-4 mt-4 p-3 bg-slate-50 rounded">
                    <div>
                      <p className="text-xs text-slate-600">Valor Total</p>
                      <p className="font-semibold text-slate-900">
                        {formatCurrency(processo.valor_total_honorarios || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Valor Pago</p>
                      <p className="font-semibold text-green-600">
                        {formatCurrency(processo.valor_pago_honorarios || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Saldo Pendente</p>
                      <p className="font-semibold text-amber-600">
                        {formatCurrency(processo.valor_pendente_honorarios || 0)}
                      </p>
                    </div>
                  </div>

                  {processo.data_distribuicao && (
                    <p className="text-xs text-slate-500 mt-2">
                      Distribuído em: {new Date(processo.data_distribuicao).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Documentos do Cliente ({documentos.length})</CardTitle>
            {documentos.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadAll}
                data-testid="download-all-button"
              >
                <FolderDown className="w-4 h-4 mr-2" />
                Baixar Todos
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {documentos.length > 0 ? (
            <div className="space-y-3">
              {documentos.map((doc) => (
                <div
                  key={doc.id}
                  className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="documento-item"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-2xl">
                        {getFileIcon(doc.nome_arquivo)}
                      </div>
                      <div className="flex-1">
                        {/* Tipo do documento - sempre visível */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full font-semibold text-sm ${
                            doc.tipo_documento 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-slate-200 text-slate-500'
                          }`}>
                            {doc.tipo_documento || 'Tipo não definido'}
                          </span>
                        </div>
                        
                        {/* Descrição - sempre visível */}
                        <p className={`text-base mb-2 ${
                          doc.descricao 
                            ? 'text-slate-900 font-medium' 
                            : 'text-slate-400 italic'
                        }`}>
                          {doc.descricao || 'Sem descrição'}
                        </p>
                        
                        {/* Nome do arquivo e metadados */}
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[300px]" title={doc.nome_arquivo}>
                            {doc.nome_arquivo}
                          </span>
                          <span>•</span>
                          <span>{formatFileSize(doc.tamanho_bytes)}</span>
                          <span>•</span>
                          <span>{new Date(doc.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {isPreviewable(doc.nome_arquivo) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreview(doc)}
                          title="Visualizar"
                          data-testid="preview-button"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(doc)}
                        title="Editar tipo e descrição"
                        data-testid="edit-button"
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(doc.id, doc.nome_arquivo)}
                        title="Download"
                        data-testid="download-button"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(doc.id)}
                        title="Excluir"
                        data-testid="delete-button"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhum documento cadastrado</p>
              <p className="text-sm mt-1">Use o botão "Upload de Documento" para adicionar arquivos</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Preview */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewDoc?.nome_arquivo}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewDoc && (
              previewDoc.nome_arquivo.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={getPreviewUrl(previewDoc.id)}
                  className="w-full h-[70vh] border-0"
                  title="Preview do documento"
                />
              ) : (
                <img
                  src={getPreviewUrl(previewDoc.id)}
                  alt={previewDoc.nome_arquivo}
                  className="max-w-full max-h-[70vh] mx-auto object-contain"
                />
              )
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => handleDownload(previewDoc?.id, previewDoc?.nome_arquivo)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button onClick={() => setIsPreviewOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição de Documento */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Documento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            {editingDoc && (
              <div className="p-3 bg-slate-50 rounded-lg mb-4">
                <p className="text-sm text-slate-600">Arquivo:</p>
                <p className="font-medium">{editingDoc.nome_arquivo}</p>
              </div>
            )}
            <div>
              <Label htmlFor="edit_tipo_documento">Tipo de Documento</Label>
              <Input
                id="edit_tipo_documento"
                placeholder="Ex: Contrato, RG, CPF, Comprovante"
                value={editData.tipo_documento}
                onChange={(e) => setEditData({ ...editData, tipo_documento: e.target.value })}
                data-testid="edit-tipo-documento-input"
              />
            </div>
            <div>
              <Label htmlFor="edit_descricao">Descrição</Label>
              <textarea
                id="edit_descricao"
                className="w-full min-h-[80px] p-2 border rounded-md"
                placeholder="Descreva o documento..."
                value={editData.descricao}
                onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                data-testid="edit-descricao-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800" 
                disabled={saving}
                data-testid="save-edit-button"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClienteDetalhes;
